import React, { useCallback, useEffect, useState } from 'react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { Award, Loader2, AlertCircle, Search, Ban, Plus, CheckCircle2, Download, FileText, Eye, X } from 'lucide-react'
import {
  issueCertificate,
  listCertificates,
  getCertificateStats,
  revokeCertificate,
  CERTIFICATE_STATUS_LABELS,
  type Certificate,
  type CertificateRequest,
  type CertificateStats,
} from '@/services/certificateService'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'
import { downloadCertificatePdf } from '@/lib/certificatePdf'

const STATUS_FILTERS: (string | 'ALL')[] = ['ALL', 'ACTIVE', 'REVOKED']

const certExportColumns: ExportColumn<Certificate>[] = [
  { header: 'ID', key: 'id' },
  { header: 'Certificate Number', key: 'certificateNumber' },
  { header: 'Volunteer Name', key: 'volunteerName' },
  { header: 'Volunteer Email', key: 'volunteerEmail' },
  { header: 'Award Title', key: 'awardTitle' },
  { header: 'Status', key: 'status' },
  { header: 'Programme Tag', key: 'programmeTag', format: (v) => (v ? String(v) : '') },
  { header: 'Issue Date', key: 'issueDate' },
  { header: 'Issued By', key: 'issuedByName', format: (v) => (v ? String(v) : '') },
  { header: 'Description', key: 'description', format: (v) => (v ? String(v).replace(/\s+/g, ' ').slice(0, 300) : '') },
]

const statusTone: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  REVOKED: 'bg-red-100 text-red-700',
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {CERTIFICATE_STATUS_LABELS[status as 'ACTIVE' | 'REVOKED'] ?? status}
    </span>
  )
}

const today = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const emptyForm: CertificateRequest = {
  volunteerName: '', volunteerEmail: '', awardTitle: '', description: '',
  programmeTag: '', issueDate: today(), issuedByName: '',
}

const CertificatesManagement = React.memo(() => {
  const [certs, setCerts] = useState<Certificate[]>([])
  const [stats, setStats] = useState<CertificateStats | Record<string, number>>({})
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<CertificateRequest>(emptyForm)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [lastIssued, setLastIssued] = useState<Certificate | null>(null)
  const [viewCert, setViewCert] = useState<Certificate | null>(null)

  const size = 15

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, s] = await Promise.all([
        listCertificates({ status: statusFilter === 'ALL' ? undefined : statusFilter, q: q || undefined, page, size }),
        getCertificateStats(),
      ])
      setCerts(data.content)
      setTotal(data.totalElements)
      setTotalPages(data.totalPages)
      setStats(s)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load certificates.')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, q, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [statusFilter, q])

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg })
    setTimeout(() => setToast(null), 3500)
  }

  const revoke = async (c: Certificate) => {
    if (!window.confirm(`Revoke certificate ${c.certificateNumber} for “${c.volunteerName}”? It will show as revoked in public verification.`)) return
    setActingId(c.id)
    try {
      await revokeCertificate(c.id)
      flash('success', `Revoked ${c.certificateNumber}`)
      await load()
    } catch (e: any) {
      flash('error', e?.response?.data?.message || 'Failed to revoke.')
    } finally {
      setActingId(null)
    }
  }

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (!form.volunteerName.trim()) errs.volunteerName = 'Volunteer name is required'
    if (!form.volunteerEmail.trim()) errs.volunteerEmail = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.volunteerEmail)) errs.volunteerEmail = 'Enter a valid email'
    if (!form.awardTitle.trim()) errs.awardTitle = 'Award title is required'
    if (form.issueDate && Number.isNaN(Date.parse(form.issueDate))) errs.issueDate = 'Enter a valid date'
    setFormErrors(errs)
    return Object.keys(errs).length === 0
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      const created = await issueCertificate({
        ...form,
        volunteerEmail: form.volunteerEmail.trim().toLowerCase(),
        volunteerName: form.volunteerName.trim(),
        awardTitle: form.awardTitle.trim(),
      })
      flash('success', `Issued ${created.certificateNumber} to ${created.volunteerName}`)
      setLastIssued(created)
      setForm(emptyForm)
      setShowForm(false)
      await load()
    } catch (err: any) {
      flash('error', err?.response?.data?.message || 'Failed to issue certificate.')
    } finally {
      setSubmitting(false)
    }
  }

  const set = (k: keyof CertificateRequest, v: string) => setForm((f) => ({ ...f, [k]: v }))

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title="Volunteer Certificates" subtitle="Issue and manage recognition certificates for volunteers. Certificates are PII-safe in public verification." icon={Award} />

        <div className="grid grid-cols-3 gap-4">
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-900">{stats.TOTAL ?? 0}</p><p className="text-sm text-gray-500">Total</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-green-600">{stats.ACTIVE ?? 0}</p><p className="text-sm text-gray-500">Active</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-red-600">{stats.REVOKED ?? 0}</p><p className="text-sm text-gray-500">Revoked</p></GovCard>
        </div>

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        {lastIssued && (
          <GovCard>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Last issued certificate — share this number with the volunteer:</p>
                <p className="font-mono text-lg font-bold text-gray-900">{lastIssued.certificateNumber}</p>
                <p className="text-sm text-gray-600 mt-1">{lastIssued.awardTitle} · {lastIssued.volunteerName}</p>
                <p className="text-xs text-gray-500 mt-1">Public verify link: /volunteer → “Verify a certificate”, or GET /api/certificates/verify/{lastIssued.certificateNumber}</p>
              </div>
              <button onClick={() => setLastIssued(null)} className="text-gray-400 hover:text-gray-700" aria-label="Dismiss">✕</button>
            </div>
          </GovCard>
        )}

        {/* Issue form */}
        <GovCard>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">Issue a certificate</h2>
            <GovButton variant="primary" size="sm" icon={showForm ? undefined : Plus} onClick={() => { setShowForm((v) => !v); if (showForm) setFormErrors({}) }}>
              {showForm ? 'Cancel' : 'New certificate'}
            </GovButton>
          </div>
          {showForm && (
            <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="c-name">Volunteer name *</label>
                <input id="c-name" value={form.volunteerName} onChange={(e) => set('volunteerName', e.target.value)} className={`w-full border rounded-md px-3 py-2 text-sm ${formErrors.volunteerName ? 'border-red-400' : 'border-gray-200'}`} maxLength={120} />
                {formErrors.volunteerName && <p className="text-xs text-red-600 mt-1">{formErrors.volunteerName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="c-email">Volunteer email *</label>
                <input id="c-email" type="email" value={form.volunteerEmail} onChange={(e) => set('volunteerEmail', e.target.value)} className={`w-full border rounded-md px-3 py-2 text-sm ${formErrors.volunteerEmail ? 'border-red-400' : 'border-gray-200'}`} maxLength={160} />
                {formErrors.volunteerEmail && <p className="text-xs text-red-600 mt-1">{formErrors.volunteerEmail}</p>}
                <p className="text-[11px] text-gray-400 mt-1">Used only for the volunteer's own “my certificates” list. Never shown in public verification.</p>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="c-award">Award title *</label>
                <input id="c-award" value={form.awardTitle} onChange={(e) => set('awardTitle', e.target.value)} className={`w-full border rounded-md px-3 py-2 text-sm ${formErrors.awardTitle ? 'border-red-400' : 'border-gray-200'}`} maxLength={160} placeholder="e.g. Outstanding TB Awareness Volunteer 2026" />
                {formErrors.awardTitle && <p className="text-xs text-red-600 mt-1">{formErrors.awardTitle}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="c-desc">Description</label>
                <textarea id="c-desc" value={form.description} onChange={(e) => set('description', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" rows={3} maxLength={5000} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="c-tag">Programme tag</label>
                <input id="c-tag" value={form.programmeTag} onChange={(e) => set('programmeTag', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" maxLength={60} placeholder="e.g. Awareness Drive" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="c-date">Issue date</label>
                <input id="c-date" type="date" value={form.issueDate} onChange={(e) => set('issueDate', e.target.value)} className={`w-full border rounded-md px-3 py-2 text-sm ${formErrors.issueDate ? 'border-red-400' : 'border-gray-200'}`} />
                {formErrors.issueDate && <p className="text-xs text-red-600 mt-1">{formErrors.issueDate}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="c-by">Issued by (name)</label>
                <input id="c-by" value={form.issuedByName} onChange={(e) => set('issuedByName', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" maxLength={120} placeholder="Name shown on the certificate" />
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <GovButton type="submit" variant="primary" disabled={submitting} icon={submitting ? Loader2 : undefined}>
                  {submitting ? 'Issuing…' : 'Issue certificate'}
                </GovButton>
              </div>
            </form>
          )}
        </GovCard>

        {/* Filters */}
        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 mr-1">Status:</span>
            {STATUS_FILTERS.map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f === 'ALL' ? 'All' : CERTIFICATE_STATUS_LABELS[f as 'ACTIVE' | 'REVOKED']}
              </button>
            ))}
            <div className="relative ml-auto">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / award / number" className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md w-56" />
            </div>
            <span className="text-xs text-gray-400">{total} total</span>
          </div>
        </GovCard>

        {/* List */}
        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Certificates</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => exportToCSV('certificates', certExportColumns, certs)} disabled={!certs.length} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"><Download className="w-3.5 h-3.5" /> CSV</button>
              <button onClick={() => exportToPDF('certificates', 'Certificates Report', certExportColumns, certs)} disabled={!certs.length} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"><FileText className="w-3.5 h-3.5" /> PDF</button>
              <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : certs.length === 0 ? (
            <GovEmptyState icon="🏅" title="No certificates" description="No certificates match this filter. Issue one above." />
          ) : (
            <div className="divide-y divide-gray-100">
              {certs.map((c) => (
                <div key={c.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <StatusBadge status={c.status} />
                        <span className="text-xs font-mono text-gray-500">{c.certificateNumber}</span>
                      </div>
                      <h4 className="font-medium text-gray-900">{c.awardTitle}</h4>
                      <p className="text-xs text-gray-500">{c.volunteerName} · {c.volunteerEmail}</p>
                      {c.description && <p className="text-sm text-gray-700 mt-1 line-clamp-2">{c.description}</p>}
                      <p className="text-[11px] text-gray-400 mt-1">
                        Issued {c.issueDate}{c.issuedByName ? ` · ${c.issuedByName}` : ''}{c.programmeTag ? ` · ${c.programmeTag}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {actingId === c.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      <button onClick={() => setViewCert(c)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="View certificate" title="View"><Eye className="w-4 h-4" /></button>
                      {c.status === 'ACTIVE' && (
                        <button onClick={() => revoke(c)} disabled={actingId === c.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Revoke"><Ban className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>
      </div>

      {/* Certificate detail modal — View opens an in-page popup with the full
          certificate details + Download PDF (certificate face) + Revoke. */}
      {viewCert && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setViewCert(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Certificate "face" header */}
            <div className="relative bg-gradient-to-br from-amber-50 to-yellow-100 border-b border-amber-200 p-6 text-center">
              <button onClick={() => setViewCert(null)} className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-700 hover:bg-white/60 rounded-lg">
                <X className="w-5 h-5" />
              </button>
              <Award className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <p className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">Certificate of Recognition</p>
              <h3 className="text-xl font-bold text-gray-900 mt-1">{viewCert.awardTitle}</h3>
              <p className="text-sm text-gray-600 mt-1">Presented to</p>
              <p className="text-lg font-semibold text-gray-900">{viewCert.volunteerName}</p>
              <p className="text-xs font-mono text-gray-500 mt-2">{viewCert.certificateNumber}</p>
            </div>

            <div className="p-4 space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Status</span>
                <StatusBadge status={viewCert.status} />
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Issue date</span>
                <span className="text-gray-900 font-medium">{viewCert.issueDate}</span>
              </div>
              {viewCert.issuedByName && (
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Issued by</span>
                  <span className="text-gray-900 font-medium">{viewCert.issuedByName}</span>
                </div>
              )}
              {viewCert.programmeTag && (
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Programme</span>
                  <span className="text-gray-900 font-medium">{viewCert.programmeTag}</span>
                </div>
              )}
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Volunteer email</span>
                <span className="text-gray-900 font-medium break-all">{viewCert.volunteerEmail}</span>
              </div>
              {viewCert.description && (
                <div className="pt-2">
                  <span className="text-gray-500 block mb-1">Description</span>
                  <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-xs">{viewCert.description}</p>
                </div>
              )}
              <p className="text-[11px] text-gray-400 pt-1">Public verify: /volunteer → “Verify a certificate”, or GET /api/certificates/verify/{viewCert.certificateNumber}</p>
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-xl">
              <button
                onClick={() => downloadCertificatePdf(viewCert)}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </button>
              {viewCert.status === 'ACTIVE' && (
                <button
                  onClick={() => { const c = viewCert; setViewCert(null); revoke(c) }}
                  disabled={actingId === viewCert.id}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                >
                  <Ban className="w-3.5 h-3.5" /> Revoke
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </GovLayout>
  )
})

CertificatesManagement.displayName = 'CertificatesManagement'
export default CertificatesManagement